import { Suspense } from 'react';

import { TasksApiProvider } from '@rtkwlf/c3-api-tasks';
import { CapabilitiesProvider } from '@rtkwlf/c3-lib-capabilities';
import { OrganizationsProvider } from '@rtkwlf/c3-lib-organizations';
import { Navigate } from 'react-router-dom';

import { FeatureFlagGuard } from '@platform/client/feature-flags';
import type { FeatureConfig } from '@platform/client/features';

import { mutations as tasksMutations } from '@apis/tasks/mutations';
import { organizations } from '@apis/tasks/organizations.store';
import { queries as tasksQueries } from '@apis/tasks/queries';

import i18n from './locales/i18n';
import { RouterProvider } from './providers/router-provider';
import { Layout } from './routes/layout';
import { OrganizationTasksListArcticWolfRoute } from './routes/organization.tasks.list.arcticwolf';
import { OrganizationTasksListOrganizationRoute } from './routes/organization.tasks.list.organization';
import { OrganizationTasksListOutstandingRoute } from './routes/organization.tasks.list.outstanding';
import { AllTasksLayout } from './routes/tasks';
import { TasksDetailsLayoutRoute } from './routes/tasks.details';
import { TasksDetailsAttachmentsRoute } from './routes/tasks.details.attachments';
import { TasksDetailsCommentsRoute } from './routes/tasks.details.comments';
import { capabilities } from './stores/capabilities';

export default {
  routes: [
    {
      path: 'tasks',
      element: (
        <RouterProvider>
          <OrganizationsProvider store={organizations}>
            <CapabilitiesProvider store={capabilities}>
              <TasksApiProvider
                queries={tasksQueries}
                mutations={tasksMutations}
              >
                <FeatureFlagGuard featureFlag="DESTRUCTICONS_VIEW_TASKS">
                  <Suspense>
                    <Layout />
                  </Suspense>
                </FeatureFlagGuard>
              </TasksApiProvider>
            </CapabilitiesProvider>
          </OrganizationsProvider>
        </RouterProvider>
      ),
      children: [
        {
          path: 'list',
          element: <AllTasksLayout />,
          children: [
            {
              index: true,
              element: <Navigate to="./outstanding" />,
            },
            {
              path: 'outstanding',
              element: <OrganizationTasksListOutstandingRoute />,
            },
            {
              path: 'arcticwolf',
              element: <OrganizationTasksListArcticWolfRoute />,
            },
            {
              path: 'organization',
              element: <OrganizationTasksListOrganizationRoute />,
            },
          ],
        },
        {
          path: ':taskId',
          element: <TasksDetailsLayoutRoute />,
          children: [
            {
              path: 'comments',
              element: <TasksDetailsCommentsRoute />,
            },
            {
              path: 'attachments',
              element: <TasksDetailsAttachmentsRoute />,
            },
          ],
        },
      ],
    },
  ],
  navigation: ({ featureFlags }) => {
    if (featureFlags?.DESTRUCTICONS_VIEW_TASKS) {
      return {
        securityJourney: {
          tasks: {
            title: i18n.t('navMenu:tasks'),
            segment: '/tasks/list',
          },
        },
      };
    }

    return {};
  },
} satisfies FeatureConfig;
